import React, { useState, useEffect, useCallback } from 'react';

const TODAY = new Date().toISOString().slice(0, 10);
const API   = '/api/audit';
const TOKEN = localStorage.getItem('nb_admin_token') || '';

// ── URL param helpers ────────────────────────────────────────────────────────
function getParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    tab:  p.get('tab')  || 'logs',
    date: p.get('date') || TODAY,
    type: p.get('type') || 'all',
    ucc:  p.get('ucc')  || '',
    page: parseInt(p.get('page') || '1'),
  };
}
function setParams(obj) {
  const p   = new URLSearchParams(window.location.search);
  Object.entries(obj).forEach(([k, v]) => v ? p.set(k, v) : p.delete(k));
  window.history.pushState({}, '', '?' + p.toString());
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtIST(val) {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
    year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
  });
}
function fmtTime(val) {
  if (!val) return '—';
  return new Date(val).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
  });
}

function authHeaders() {
  const t = localStorage.getItem('nb_admin_token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${t}`, 'x-audit-token': t };
}

// ── Action badge colours ─────────────────────────────────────────────────────
function actionBadge(action) {
  const a = (action || '').toUpperCase();
  if (a.includes('SUCCESS') || a.includes('SYNC'))     return { bg: '#e1f5ee', col: '#085041', bdr: '#1D9E75' };
  if (a.includes('FAILED') || a.includes('REJECT'))    return { bg: '#FEF2F2', col: '#991B1B', bdr: '#DC2626' };
  if (a.includes('OTP') || a.includes('EMAIL') || a.includes('SMS')) return { bg: '#EEF2FF', col: '#1338A8', bdr: '#1B4FD8' };
  if (a.includes('SQUAREOFF') || a.includes('ORDER'))  return { bg: '#FFFBEB', col: '#92400E', bdr: '#D97706' };
  if (a.includes('UPLOAD') || a.includes('BF'))        return { bg: '#EEEDFE', col: '#26215C', bdr: '#534AB7' };
  if (a.includes('SSO'))                               return { bg: '#FFF7ED', col: '#C2410C', bdr: '#EA580C' };
  return { bg: '#F0F2F5', col: '#5A6272', bdr: '#CBD5E0' };
}
function typeBadge(type) {
  const t = (type || '').toUpperCase();
  if (t === 'CLIENT')   return { bg: '#E0F2FE', col: '#0C4A6E' };
  if (t === 'ADMIN')    return { bg: '#EEEDFE', col: '#3C3489' };
  if (t === 'DEALER')   return { bg: '#FFF7ED', col: '#9A3412' };
  if (t === 'ORDER')    return { bg: '#FFFBEB', col: '#78350F' };
  if (t === 'DROPCOPY') return { bg: '#F0FDF4', col: '#14532D' };
  return { bg: '#F0F2F5', col: '#374151' };
}

const s = {
  wrap:     { fontFamily: "'DM Sans',-apple-system,sans-serif", fontSize: 13, color: '#0D0F12', minHeight: '80vh' },
  hdr:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 },
  title:    { fontSize: 17, fontWeight: 600, color: '#0D0F12' },
  sub:      { fontSize: 12, color: '#5A6272', marginTop: 2 },
  tabRow:   { display: 'flex', gap: 0, background: '#fff', border: '0.5px solid #E2E8F0', borderRadius: 8, overflow: 'hidden', marginBottom: 14 },
  tab:      (a) => ({ border: 'none', background: a ? '#1B4FD8' : 'transparent', color: a ? '#fff' : '#5A6272', padding: '9px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", borderRight: '0.5px solid #E2E8F0' }),
  card:     { background: '#fff', border: '0.5px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 12 },
  th:       { background: '#F8FAFC', borderBottom: '0.5px solid #E2E8F0', color: '#64748B', fontSize: 10, fontWeight: 600, padding: '8px 12px', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' },
  td:       { color: '#0D0F12', fontSize: 12, padding: '9px 12px', verticalAlign: 'middle', borderBottom: '0.5px solid #F1F5F9' },
  badge:    (c) => ({ display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: c.bg, color: c.col, border: `0.5px solid ${c.bdr||c.col}`, whiteSpace: 'nowrap' }),
  typPill:  (c) => ({ display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: c.bg, color: c.col }),
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 8, marginBottom: 14 },
  stat:     (active) => ({ background: active ? '#EEF2FF' : '#fff', border: `0.5px solid ${active ? '#1B4FD8' : '#E2E8F0'}`, borderRadius: 8, padding: '10px 12px', cursor: 'pointer' }),
  statLbl:  { fontSize: 10, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' },
  statNum:  (active) => ({ fontSize: 22, fontWeight: 500, color: active ? '#1B4FD8' : '#0D0F12', marginTop: 2 }),
  input:    { border: '0.5px solid #CBD5E0', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontFamily: "'DM Sans',sans-serif", color: '#0D0F12', background: '#fff' },
  btnPri:   { background: '#1B4FD8', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
  btnSec:   { background: '#fff', color: '#1B4FD8', border: '0.5px solid #1B4FD8', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
  btnLink:  { background: 'none', border: 'none', color: '#1B4FD8', cursor: 'pointer', fontSize: 12, textDecoration: 'underline', fontFamily: "'DM Sans',sans-serif", padding: 0 },
  back:     { background: 'none', border: 'none', color: '#1B4FD8', cursor: 'pointer', fontSize: 12, fontFamily: "'DM Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 4, padding: 0, marginBottom: 12 },
  empty:    { padding: 40, textAlign: 'center', color: '#9BA3B0', fontSize: 13 },
  mono:     { fontFamily: 'monospace', fontSize: 11 },
  info:     { background: '#EEF2FF', border: '0.5px solid #C7D2FE', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#1338A8', marginBottom: 10 },
  err:      { background: '#FEF2F2', border: '0.5px solid #FECACA', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#991B1B', marginBottom: 10 },
};

// ═══════════════════════════════════════════════════════════════════════════════
export default function AuditLogs({ adminT }) {
  const T = adminT || {};
  const [params, setParamsState] = useState(getParams());
  const [logs,    setLogs]    = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [orders,  setOrders]  = useState([]);
  const [client,  setClient]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // Keep params in sync with URL (browser back/forward)
  useEffect(() => {
    const handler = () => setParamsState(getParams());
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // Navigate helper
  const navigate = useCallback((obj) => {
    const merged = { ...params, ...obj };
    setParams(merged);
    setParamsState(merged);
  }, [params]);

  // ── Load data based on current params ──────────────────────────────────────
  useEffect(() => {
    const { tab, date, type, ucc, page } = params;
    setError('');

    if (tab === 'client' && ucc) {
      loadClientActivity(date, ucc);
    } else if (tab === 'orders') {
      loadOrders(date);
    } else {
      loadLogs(date, type, page);
    }
  }, [params]);

  async function loadLogs(date, type, page) {
    setLoading(true);
    try {
      const r = await fetch(`${API}/logs?date=${date}&type=${type}&page=${page}&limit=50`, { headers: authHeaders() });
      const d = await r.json();
      if (d.success) { setLogs(d.logs); setSummary(d.summary); setPagination(d.pagination); }
      else setError(d.error || 'Failed to load logs');
    } catch (e) { setError('Network error: ' + e.message); }
    setLoading(false);
  }

  async function loadOrders(date) {
    setLoading(true);
    try {
      const r = await fetch(`${API}/orders?date=${date}`, { headers: authHeaders() });
      const d = await r.json();
      if (d.success) setOrders(d.orders);
      else setError(d.error || 'Failed to load orders');
    } catch (e) { setError('Network error: ' + e.message); }
    setLoading(false);
  }

  async function loadClientActivity(date, ucc) {
    setLoading(true);
    setClient(null);
    try {
      const r = await fetch(`${API}/client-activity?date=${date}&ucc=${ucc}`, { headers: authHeaders() });
      const d = await r.json();
      if (d.success) setClient(d);
      else setError(d.error || 'Failed to load client activity');
    } catch (e) { setError('Network error: ' + e.message); }
    setLoading(false);
  }

  function downloadCSV(endpoint, filename) {
    const { date } = params;
    const url = `${API}/${endpoint}?date=${date}&format=csv`;
    const a   = document.createElement('a');
    a.href    = url + `&token=${localStorage.getItem('nb_admin_token') || ''}`;
    a.download = filename + `_${date}.csv`;
    a.click();
  }

  // ── Stat cards ─────────────────────────────────────────────────────────────
  const statCards = summary ? [
    { key: 'all',      label: 'Total events',    val: summary.total_events },
    { key: 'client',   label: 'Client logins',   val: summary.client_logins },
    { key: 'admin',    label: 'Admin actions',   val: summary.admin_actions },
    { key: 'dealer',   label: 'Dealer actions',  val: summary.dealer_actions },
    { key: 'order',    label: 'Sq-off orders',   val: summary.squareoffs_placed },
    { key: 'dropcopy', label: 'Sync events',     val: summary.day_pos_syncs },
  ] : [];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={s.wrap}>

      {/* Header */}
      <div style={s.hdr}>
        <div>
          <div style={s.title}>Audit Logs</div>
          <div style={s.sub}>Complete activity trail · {params.date}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={params.date} max={TODAY} style={s.input}
            onChange={e => navigate({ date: e.target.value, page: 1, tab: 'logs', ucc: '', type: 'all' })} />
          {params.tab !== 'client' && (
            <button style={s.btnSec}
              onClick={() => downloadCSV(params.tab === 'orders' ? 'orders' : 'logs', params.tab === 'orders' ? 'navia_orders' : 'navia_audit')}>
              ↓ Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <nav style={s.tabRow}>
        {[
          { key: 'logs',   label: '📋 Activity logs' },
          { key: 'orders', label: '📦 Sq-off orders' },
        ].map((t, i) => (
          <button key={t.key} style={{ ...s.tab(params.tab === t.key), borderRight: i === 1 ? 'none' : '0.5px solid #E2E8F0' }}
            onClick={() => navigate({ tab: t.key, ucc: '', page: 1 })}>
            {t.label}
          </button>
        ))}
        {params.tab === 'client' && params.ucc && (
          <button style={{ ...s.tab(true), borderRight: 'none' }}>👤 {params.ucc}</button>
        )}
      </nav>

      {error && <div style={s.err}>⚠ {error}</div>}

      {/* ── ACTIVITY LOGS TAB ───────────────────────────────────────────────── */}
      {params.tab === 'logs' && (
        <>
          {/* Summary stat cards */}
          {summary && (
            <div style={s.statGrid}>
              {statCards.map(sc => (
                <div key={sc.key} style={s.stat(params.type === sc.key)}
                  onClick={() => navigate({ type: sc.key, page: 1 })}>
                  <div style={s.statLbl}>{sc.label}</div>
                  <div style={s.statNum(params.type === sc.key)}>{sc.val}</div>
                </div>
              ))}
            </div>
          )}

          {/* Info bar */}
          {summary && (
            <div style={s.info}>
              📅 {params.date} · {summary.unique_clients} unique clients · {summary.total_events} events
              {params.type !== 'all' && <> · Filter: <b>{params.type}</b></>}
              {params.type !== 'all' && (
                <button style={{ ...s.btnLink, marginLeft: 8 }} onClick={() => navigate({ type: 'all', page: 1 })}>
                  Clear filter
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div style={s.empty}>Loading logs…</div>
          ) : logs.length === 0 ? (
            <div style={s.empty}>No activity found for {params.date}</div>
          ) : (
            <>
              <div style={s.card}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                    <thead>
                      <tr>
                        {['Time (IST)', 'Type', 'Actor', 'UCC / Client', 'Action', 'OTP / Details', 'IP Address', 'Status'].map(h => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, i) => {
                        const ab = actionBadge(log.action);
                        const tb = typeBadge(log.actor_type);
                        return (
                          <tr key={i} style={{ borderBottom: '0.5px solid #F1F5F9' }}>
                            <td style={{ ...s.td, ...s.mono, whiteSpace: 'nowrap' }}>{fmtIST(log.created_at)}</td>
                            <td style={s.td}><span style={s.typPill(tb)}>{log.actor_type}</span></td>
                            <td style={{ ...s.td, fontWeight: 500 }}>{log.actor || '—'}</td>
                            <td style={s.td}>
                              {log.ucc ? (
                                <button style={s.btnLink} onClick={() => navigate({ tab: 'client', ucc: log.ucc })}>
                                  {log.ucc}{log.name ? ` · ${log.name}` : ''}
                                </button>
                              ) : <span style={{ color: '#9BA3B0' }}>—</span>}
                            </td>
                            <td style={s.td}><span style={s.badge(ab)}>{log.action || '—'}</span></td>
                            <td style={{ ...s.td, maxWidth: 280, fontSize: 11, color: '#374151' }}>
                              {/* Highlight OTP in details */}
                              {log.details ? (
                                <span title={log.details}>
                                  {log.details.length > 80 ? log.details.slice(0, 80) + '…' : log.details}
                                </span>
                              ) : '—'}
                            </td>
                            <td style={{ ...s.td, ...s.mono, fontSize: 11 }}>{log.ip || '—'}</td>
                            <td style={s.td}>
                              <span style={{
                                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                                background: log.status === 'SUCCESS' ? '#e1f5ee' : log.status === 'FAILED' ? '#FEF2F2' : '#F0F2F5',
                                color: log.status === 'SUCCESS' ? '#085041' : log.status === 'FAILED' ? '#991B1B' : '#374151',
                              }}>{log.status || '—'}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {pagination && pagination.total_pages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: 12, color: '#5A6272' }}>
                    Page {pagination.page} of {pagination.total_pages} · {pagination.total} records
                  </span>
                  <button style={s.btnSec} disabled={params.page <= 1}
                    onClick={() => navigate({ page: params.page - 1 })}>← Prev</button>
                  <button style={s.btnSec} disabled={!pagination.has_next}
                    onClick={() => navigate({ page: params.page + 1 })}>Next →</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── ORDERS TAB ──────────────────────────────────────────────────────── */}
      {params.tab === 'orders' && (
        <>
          {loading ? <div style={s.empty}>Loading orders…</div>
          : orders.length === 0 ? <div style={s.empty}>No sq-off orders for {params.date}</div>
          : (
            <>
              <div style={s.info}>
                📦 {orders.length} sq-off orders on {params.date}
              </div>
              <div style={s.card}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                    <thead>
                      <tr>
                        {['Time (IST)', 'UCC', 'Client', 'Mobile', 'Exchange / Segment', 'Symbol', 'Side', 'Qty', 'Type', 'Status', 'Placed by', 'Dealer'].map(h => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o, i) => (
                        <tr key={i} style={{ borderBottom: '0.5px solid #F1F5F9' }}>
                          <td style={{ ...s.td, ...s.mono, whiteSpace: 'nowrap', fontSize: 11 }}>{fmtIST(o.placed_at)}</td>
                          <td style={s.td}>
                            <button style={s.btnLink} onClick={() => navigate({ tab: 'client', ucc: o.ucc })}>
                              {o.ucc}
                            </button>
                          </td>
                          <td style={{ ...s.td, fontWeight: 500 }}>{o.client_name || '—'}</td>
                          <td style={{ ...s.td, ...s.mono }}>{o.mobile || '—'}</td>
                          <td style={s.td}>{o.exchange} / {o.segment}</td>
                          <td style={{ ...s.td, fontWeight: 600 }}>{o.symbol}</td>
                          <td style={{ ...s.td, color: o.side === 'SELL' ? '#DC2626' : '#059669', fontWeight: 600 }}>{o.side}</td>
                          <td style={s.td}>{o.quantity}</td>
                          <td style={s.td}>{o.order_type}</td>
                          <td style={s.td}>
                            <span style={{
                              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                              background: o.status === 'ORDER_TRADED' ? '#e1f5ee' : o.status === 'FILE_GENERATED' ? '#EEF2FF' : '#FFFBEB',
                              color: o.status === 'ORDER_TRADED' ? '#085041' : o.status === 'FILE_GENERATED' ? '#1338A8' : '#92400E',
                            }}>{o.status}</span>
                          </td>
                          <td style={s.td}>{o.placed_by || 'CLIENT'}</td>
                          <td style={{ ...s.td, color: '#5A6272' }}>{o.dealer_id || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── CLIENT DRILL-DOWN TAB ────────────────────────────────────────────── */}
      {params.tab === 'client' && (
        <>
          <button style={s.back} onClick={() => navigate({ tab: 'logs', ucc: '' })}>
            ← Back to logs
          </button>

          {loading ? <div style={s.empty}>Loading client activity…</div>
          : !client ? <div style={s.empty}>No data found for {params.ucc}</div>
          : (
            <>
              {/* Client info card */}
              <div style={{ background: '#fff', border: '0.5px solid #E2E8F0', borderRadius: 10, padding: '14px 18px', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 44, height: 44, background: '#EEF2FF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 600, color: '#1B4FD8', flexShrink: 0 }}>
                      {(client.client?.client_name || client.client?.ucc || 'C')[0]}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{client.client?.client_name || '—'}</div>
                      <div style={{ fontSize: 12, color: '#5A6272', marginTop: 2 }}>UCC: {client.client?.ucc}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Mobile', val: client.client?.mobile ? `+91 ****${String(client.client.mobile).slice(-4)}` : '—' },
                      { label: 'Email',  val: client.client?.email ? `${client.client.email[0]}*****@${client.client.email.split('@')[1]}` : '—' },
                    ].map(row => (
                      <div key={row.label} style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 10, color: '#64748B', textTransform: 'uppercase', fontWeight: 600 }}>{row.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{row.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Summary stat cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: 8, marginBottom: 14 }}>
                {[
                  { label: 'Orders placed',   val: client.summary?.orders_placed || 0 },
                  { label: 'Traded',           val: client.summary?.traded_orders || 0 },
                  { label: 'Pending',          val: client.summary?.pending_orders || 0 },
                  { label: 'Day positions',    val: client.summary?.day_positions || 0 },
                  { label: 'B/F positions',    val: client.summary?.bf_positions || 0 },
                ].map(sc => (
                  <div key={sc.label} style={{ background: '#F8FAFC', border: '0.5px solid #E2E8F0', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={s.statLbl}>{sc.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 500, marginTop: 2 }}>{sc.val}</div>
                  </div>
                ))}
              </div>

              {/* Login & OTP activity from system_logs */}
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#0D0F12' }}>
                🔐 Login & OTP activity — {params.date}
              </div>
              {(() => {
                const loginLogs = logs.filter(l => l.ucc === params.ucc || l.actor === params.ucc);
                if (loginLogs.length === 0) return <div style={{ ...s.empty, padding: 20, fontSize: 12 }}>No login/OTP activity in loaded logs for this date. <button style={s.btnLink} onClick={() => loadLogs(params.date, 'client', 1)}>Load client logs</button></div>;
                return (
                  <div style={{ ...s.card, marginBottom: 14 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        {['Time (IST)', 'Action', 'Details (OTP / SMS / Email)', 'IP', 'Status'].map(h => <th key={h} style={s.th}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {loginLogs.map((l, i) => (
                          <tr key={i} style={{ borderBottom: '0.5px solid #F1F5F9' }}>
                            <td style={{ ...s.td, ...s.mono, fontSize: 11, whiteSpace: 'nowrap' }}>{fmtIST(l.created_at)}</td>
                            <td style={s.td}><span style={s.badge(actionBadge(l.action))}>{l.action}</span></td>
                            <td style={{ ...s.td, fontSize: 11, maxWidth: 320 }}>{l.details || '—'}</td>
                            <td style={{ ...s.td, ...s.mono, fontSize: 11 }}>{l.ip || '—'}</td>
                            <td style={s.td}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                                background: l.status === 'SUCCESS' ? '#e1f5ee' : '#FEF2F2',
                                color: l.status === 'SUCCESS' ? '#085041' : '#991B1B' }}>{l.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Orders */}
              {client.orders?.length > 0 && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, marginTop: 16 }}>📦 Square-off orders</div>
                  <div style={s.card}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                      <thead><tr>
                        {['Time (IST)', 'Exchange', 'Symbol', 'Side', 'Qty', 'Status', 'Placed by', 'Dealer'].map(h => <th key={h} style={s.th}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {client.orders.map((o, i) => (
                          <tr key={i} style={{ borderBottom: '0.5px solid #F1F5F9' }}>
                            <td style={{ ...s.td, ...s.mono, fontSize: 11 }}>{fmtIST(o.placed_at)}</td>
                            <td style={s.td}>{o.exchange} / {o.segment}</td>
                            <td style={{ ...s.td, fontWeight: 600 }}>{o.symbol}</td>
                            <td style={{ ...s.td, color: o.side === 'SELL' ? '#DC2626' : '#059669', fontWeight: 600 }}>{o.side}</td>
                            <td style={s.td}>{o.quantity}</td>
                            <td style={s.td}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                                background: o.status === 'ORDER_TRADED' ? '#e1f5ee' : '#FFFBEB',
                                color: o.status === 'ORDER_TRADED' ? '#085041' : '#92400E' }}>{o.status}</span>
                            </td>
                            <td style={s.td}>{o.placed_by || 'CLIENT'}</td>
                            <td style={{ ...s.td, color: '#5A6272' }}>{o.dealer_id || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Day positions */}
              {client.day_positions?.length > 0 && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, marginTop: 16 }}>📊 Day positions</div>
                  <div style={s.card}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                      <thead><tr>
                        {['Symbol', 'Type', 'Expiry', 'Strike', 'Opt', 'Buy qty', 'Sell qty', 'Net qty', 'Avg buy', 'Avg sell'].map(h => <th key={h} style={s.th}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {client.day_positions.map((p, i) => (
                          <tr key={i} style={{ borderBottom: '0.5px solid #F1F5F9' }}>
                            <td style={{ ...s.td, fontWeight: 600 }}>{p.symbol}</td>
                            <td style={s.td}>{p.instrument_type}</td>
                            <td style={{ ...s.td, fontSize: 11 }}>{p.expiry_date ? new Date(p.expiry_date).toLocaleDateString('en-IN') : '—'}</td>
                            <td style={s.td}>{p.strike_price || '—'}</td>
                            <td style={s.td}>{p.option_type || '—'}</td>
                            <td style={{ ...s.td, color: '#059669', fontWeight: 500 }}>{p.buy_qty}</td>
                            <td style={{ ...s.td, color: '#DC2626', fontWeight: 500 }}>{p.sell_qty}</td>
                            <td style={{ ...s.td, fontWeight: 600 }}>{p.net_qty}</td>
                            <td style={s.td}>{p.avg_buy_price ? `₹${Number(p.avg_buy_price).toFixed(2)}` : '—'}</td>
                            <td style={s.td}>{p.avg_sell_price ? `₹${Number(p.avg_sell_price).toFixed(2)}` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* B/F positions */}
              {client.bf_positions?.length > 0 && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, marginTop: 16 }}>📂 B/F positions</div>
                  <div style={s.card}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                      <thead><tr>
                        {['Symbol', 'Type', 'Expiry', 'Strike', 'Opt', 'Net qty (lots)', 'Exchange'].map(h => <th key={h} style={s.th}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {client.bf_positions.map((p, i) => (
                          <tr key={i} style={{ borderBottom: '0.5px solid #F1F5F9' }}>
                            <td style={{ ...s.td, fontWeight: 600 }}>{p.symbol}</td>
                            <td style={s.td}>{p.instrument_type}</td>
                            <td style={{ ...s.td, fontSize: 11 }}>{p.expiry_date ? new Date(p.expiry_date).toLocaleDateString('en-IN') : '—'}</td>
                            <td style={s.td}>{p.strike_price || '—'}</td>
                            <td style={s.td}>{p.option_type || '—'}</td>
                            <td style={{ ...s.td, fontWeight: 500 }}>{p.qty_in_lots ?? p.total_open_qty ?? 0}</td>
                            <td style={s.td}>{p.exchange || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}